import { getPostalCodes } from '@/services/api'
import type { PostalCodeOption } from '@/composables/usePostalCode'

export type PostalCodeSearchStatus = 'ok' | 'empty' | 'error'

export interface PostalCodeSearchOutcome {
  status: PostalCodeSearchStatus
  items: PostalCodeOption[]
  message?: string
}

export type PostalCodeSearchProvider = (
  query: string,
  options?: { signal?: AbortSignal },
) => Promise<PostalCodeSearchOutcome>

function mapLegacyResults(
  results: Awaited<ReturnType<typeof getPostalCodes>>,
): PostalCodeOption[] {
  return results.map((item) => {
    const location = [item.settlement, item.municipality, item.state].filter(Boolean).join(', ')
    return {
      code: item.code,
      location,
      state: item.state || '',
      city: item.municipality || '',
      displayValue: `${item.code} - ${location}`,
      idEstado: item.idEstado,
      idCiudad: item.idCiudad,
      idMunicipio: item.idMunicipio,
    }
  })
}

export const legacyPostalCodeSearch: PostalCodeSearchProvider = async (query) => {
  try {
    const results = await getPostalCodes(query)
    const items = mapLegacyResults(results).slice(0, 10)
    if (items.length === 0) {
      return { status: 'empty', items: [], message: 'Sin coincidencias para este código postal.' }
    }
    return { status: 'ok', items }
  } catch {
    return {
      status: 'error',
      items: [],
      message: 'No se pudo buscar el código postal. Intenta de nuevo.',
    }
  }
}

let overrideProvider: PostalCodeSearchProvider | null = null

/** Injectable override. Pass null to restore the default (KC postal catalog). */
export function setPostalCodeSearchProvider(provider: PostalCodeSearchProvider | null): void {
  overrideProvider = provider
}

export function resolvePostalCodeSearchProvider(): PostalCodeSearchProvider {
  if (overrideProvider) return overrideProvider
  return legacyPostalCodeSearch
}
